import { AgBytesAPI } from "@ai360/core";

const EMPTY_CROPS: AgBytesAPI.ICropSelectionListItem[] = [];

export interface IUseCropListOptions {
    customerGuid?: string | null;
    isEnabled?: boolean;
    orgLevelGuid?: string | null;
}

export interface IUseCropListResult {
    crops: AgBytesAPI.ICropSelectionListItem[];
    isFetching: boolean;
}

/**
 * One RTK cache entry over `agbytes/crop/v1.0/selection-list`. Use this instead of a per-surface
 * fetch, so identical arguments share one request.
 */
export const useCropList = ({
    customerGuid = null,
    isEnabled = true,
    orgLevelGuid = null,
}: IUseCropListOptions): IUseCropListResult => {
    const isScopeResolvable = Boolean(customerGuid || orgLevelGuid);
    const { data: crops = EMPTY_CROPS, isFetching } = AgBytesAPI.useGetCropSelectionListQuery(
        {
            ...(customerGuid && { customerGuid }),
            ...(!customerGuid && orgLevelGuid != null && { orgLevelGuid }),
        },
        { skip: !isEnabled || !isScopeResolvable, refetchOnMountOrArgChange: true }
    );

    return { crops, isFetching };
};

export interface ICropDropdownOption {
    activeYn: boolean;
    defaultMapColor: string;
    guid: string;
    id: string;
    isAgvanceLinked: boolean | null;
    name: string;
}

/**
 * Narrows a `useCropList()` result to the plain option shape crop consumers expect (the same shape
 * `AgBytesItem`/legacy `cropData` supplied: `guid`, `id`, `name`, `activeYn`, `defaultMapColor`), plus
 * `isAgvanceLinked` for visibility.
 */
export const toCropDropdownOptions = (
    crops: AgBytesAPI.ICropSelectionListItem[]
): ICropDropdownOption[] =>
    crops.map((crop) => ({
        activeYn: crop.activeYn,
        defaultMapColor: crop.defaultMapColor,
        guid: crop.cropGuid,
        id: String(crop.cropId),
        isAgvanceLinked: crop.isAgvanceLinked,
        name: crop.name,
    }));

/**
 * Mirrors `blending-utils.tsx`'s `isProductUnlinked` -- a plain function, not a hook, so it can be
 * called from `mapStateToProps`, sagas, or a class `render()`, not just function components.
 */
export const isCropUnlinked = (
    cropGuid: string | null | undefined,
    crops: ICropDropdownOption[]
): boolean => {
    if (!cropGuid) {
        return false;
    }
    const crop = crops.find((c) => c.guid === cropGuid);
    return crop != null && crop.isAgvanceLinked === false;
};
